import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DataTable, { compareValues, MobileSearchBox } from '../components/DataTable';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import useSharePointList from '../hooks/useSharePointList';
import useAuth from '../auth/useAuth';
import useIsMobile from '../auth/useIsMobile';
import { createTeamMember, listTeam } from '../services/team';
import { listPlants } from '../services/plants';
import TeamForm from './TeamForm';
import { formatDateShort, formatPhone } from '../utils/formatters';
import {
  displayFirstName,
  displayFullName,
  displayStoredName,
} from '../utils/teamDisplay';

/**
 * Proxemics Team index. Directors can add new members; all roles can click
 * through to a member's detail page. The detail page holds the long tail of
 * fields (personal info, emergency contact, QR code, etc.); this table stays
 * deliberately narrow so it's scannable.
 */
export default function Team() {
  const { isDirector, isRegionalManager, email } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading, error, refresh } = useSharePointList(listTeam);
  // "View Your Team" on the dashboard links here with ?reports=mine, which
  // narrows the roster to just the people who report to the signed-in
  // Regional Manager. The sidebar's "Proxemics Team" link (no param) still
  // shows the full directory.
  const reportsMine = searchParams.get('reports') === 'mine';

  // The signed-in user's stored legal name ("First Last"), resolved from their
  // own team record by email. RegionalManager is stored in this legal form, so
  // we match reports against it. Empty when we can't find their record.
  const me = useMemo(() => {
    const lower = (email || '').toLowerCase();
    return (
      (data || []).find(
        (m) =>
          (m.Email && m.Email.toLowerCase() === lower) ||
          (m.PersonalEmail && m.PersonalEmail.toLowerCase() === lower),
      ) || null
    );
  }, [data, email]);
  const myName = me
    ? [me.FirstName, me.LastName].filter(Boolean).join(' ').trim()
    : '';
  // The signed-in user's own Regional Manager (their manager) — shown on the
  // "Your Team" view for a Liaison Manager.
  const myRegionalManager = (me?.RegionalManager || '').trim();

  // "My reports" scope (per Jillian, Aug 2026 — was RM-only):
  //   • Regional Manager → everyone whose Regional Manager is them.
  //   • Liaison Manager  → liaisons who list them as a Liaison Manager,
  //     PLUS their own Regional Manager (their manager).
  // If we can't resolve their name, fall back to the full list rather than
  // showing an empty page.
  const reportsFilter = reportsMine && myName;
  const inScope = (m) => {
    if (!reportsFilter) return true;
    const mn = myName.toLowerCase();
    // People whose Regional Manager is me.
    if ((m.RegionalManager || '').trim().toLowerCase() === mn) return true;
    // Liaisons who list me as a Liaison Manager.
    const lms = Array.isArray(m.liaisonManagers) ? m.liaisonManagers : [];
    if (lms.some((n) => (n || '').trim().toLowerCase() === mn)) return true;
    // My own Regional Manager (my manager).
    if (myRegionalManager) {
      const memberName = (
        m.Title || `${m.FirstName || ''} ${m.LastName || ''}`
      )
        .trim()
        .toLowerCase();
      if (memberName === myRegionalManager.toLowerCase()) return true;
    }
    return false;
  };

  // Exited team members are hidden from the main table so it reflects the
  // current roster. Directors and Regional Managers still need the history
  // (re-onboarding, reference checks, tenure questions), so we surface it
  // as a collapsed section below the table for those two roles only.
  const canSeeExited = isDirector || isRegionalManager;
  // Members On Leave get their own collapsed section above Exited, shown
  // to the same roles (per Jillian, Oct 2026).
  const { activeMembers, onLeaveMembers, exitedMembers } = useMemo(() => {
    const active = [];
    const onLeave = [];
    const exited = [];
    for (const m of data || []) {
      if (!inScope(m)) continue;
      if (m.EmploymentStatus === 'Exited') exited.push(m);
      else if (m.EmploymentStatus === 'On Leave') onLeave.push(m);
      else active.push(m);
    }
    return { activeMembers: active, onLeaveMembers: onLeave, exitedMembers: exited };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, reportsFilter, myName, myRegionalManager]);

  const [plants, setPlants] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listPlants();
        if (!cancelled) setPlants(rows || []);
      } catch {
        // Non-fatal — form falls back to a blank plant list.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Single search box above the grouped tables — typing once filters every
  // portal-role section simultaneously, so the user doesn't have to hunt
  // across four separate search inputs.
  const [q, setQ] = useState('');
  // Sort state matches DataTable's shape: { key, dir: 'asc' | 'desc' }. It's
  // applied within each group so clicking a column header sorts each
  // section's rows independently without breaking the role-based grouping.
  const [sort, setSort] = useState(null);

  const isSortable = (c) =>
    c.sortable !== false && typeof c.key === 'string' && !c.key.startsWith('_');

  const toggleSort = (col) => {
    if (!isSortable(col)) return;
    setSort((cur) => {
      if (!cur || cur.key !== col.key) return { key: col.key, dir: 'asc' };
      if (cur.dir === 'asc') return { key: col.key, dir: 'desc' };
      return null; // third click clears back to default (last-name) order
    });
  };

  const sortRows = (rows) => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const extract = col.sortValue || ((row) => row[col.key]);
    const copy = [...rows];
    copy.sort((a, b) => compareValues(extract(a), extract(b)));
    if (sort.dir === 'desc') copy.reverse();
    return copy;
  };
  // After a successful create, show a confirmation with the Entra reminder
  // instead of just silently closing the modal — otherwise the director has
  // to remember on their own that a role still needs to be assigned.
  const [createdMember, setCreatedMember] = useState(null);

  const onNew = () => {
    setSaveError(null);
    setCreating(true);
  };

  const onSave = useCallback(
    async (model) => {
      setSaving(true);
      setSaveError(null);
      try {
        const created = await createTeamMember(model);
        setCreating(false);
        await refresh();
        setCreatedMember({ ...model, ...created });
      } catch (err) {
        console.error('[Team] createTeamMember failed', err);
        // Graph surfaces "Item not found" when the TeamMembers list URL
        // doesn't resolve — usually a sign-in / token / list-name mismatch
        // rather than anything the director can fix by retyping fields.
        // Translate it into something actionable instead of the raw Graph
        // string so she knows what to do next.
        const rawCode = err?.body?.error?.code || err?.code || '';
        const rawMsg =
          err?.body?.error?.message || err?.message || '';
        let friendly;
        if (
          rawCode === 'itemNotFound' ||
          /item not found/i.test(rawMsg)
        ) {
          friendly =
            "Couldn't save — SharePoint returned \"Item not found\". " +
            'Try signing out and back in; if that doesn\'t fix it, the ' +
            'TeamMembers list may need to be re-provisioned ' +
            '(`npm run provision-lists`).';
        } else {
          friendly =
            rawMsg || 'Could not save. Check the browser console for details.';
        }
        setSaveError(friendly);
      } finally {
        setSaving(false);
      }
    },
    [refresh],
  );

  // Map plant codes → display names so we can show each assignment as a
  // readable badge instead of an opaque code. Stable identity via useMemo
  // so the columns array doesn't re-build on every render.
  const plantByCode = useMemo(() => {
    const map = {};
    for (const p of plants) if (p.PlantCode) map[p.PlantCode] = p;
    return map;
  }, [plants]);

  // Roster used by the RM and Liaison Manager columns to resolve stored
  // legal-form names back to roster members so the nickname (if any) can
  // be substituted for display. The stored strings stay canonical; this
  // is a render-only lookup.
  const roster = data || [];

  const columns = useMemo(
    () => [
      {
        key: 'LastName',
        label: 'Last name',
        // Widen Last Name + First Name since the Role column was pulled —
        // reclaim that space for the two columns people actually scan by.
        // nowrap keeps hyphenated / double names ("García-Lopez") on one row.
        width: '145px',
        render: (r) => (
          <span style={{ whiteSpace: 'nowrap' }}>{r.LastName || ''}</span>
        ),
      },
      {
        key: 'FirstName',
        label: 'First name',
        width: '145px',
        // Show the nickname when set; fall back to the legal FirstName.
        // Sort by the display value so alphabetical order matches what the
        // user actually sees in the column. A hover tooltip reveals the
        // legal name when a nickname is being substituted in.
        sortValue: (r) => (displayFirstName(r) || '').toLowerCase(),
        render: (r) => {
          const shown = displayFirstName(r);
          const legal = (r.FirstName || '').trim();
          if (!shown) return '';
          const title = legal && legal !== shown ? `Legal: ${legal}` : undefined;
          return (
            <span title={title} style={{ whiteSpace: 'nowrap' }}>
              {shown}
            </span>
          );
        },
      },
      {
        key: 'Email',
        label: 'Email',
        mobileHidden: true,
        // Show just the local part (before the "@") so this column doesn't
        // blow out the table width — e.g. "adam.ali" instead of
        // "adam.ali@proxemicsconsulting.com". Full address is kept in the
        // title attribute so hover reveals it, and sorting uses the full
        // address for stable behavior.
        sortValue: (r) => (r.Email || '').toLowerCase(),
        render: (r) => {
          const email = r.Email || '';
          if (!email) return <span className="muted">—</span>;
          const local = email.split('@')[0];
          return <span title={email}>{local}</span>;
        },
      },
      {
        key: 'Phone',
        label: 'Phone',
        // formatPhone renders US numbers as "(XXX) XXX-XXXX" and any
        // value starting with "+" as "+CC CC CCCC CCCC". If a legacy
        // row has an odd shape, the raw value is returned unchanged.
        // whiteSpace: nowrap keeps the number on a single line so the
        // "(555)" prefix doesn't wrap to its own row when the column
        // gets tight.
        render: (r) => {
          const pretty = formatPhone(r.Phone);
          if (!pretty) return <span className="muted">—</span>;
          return <span style={{ whiteSpace: 'nowrap' }}>{pretty}</span>;
        },
      },
      {
        key: 'RegionalManager',
        label: 'Regional Manager',
        // Trimmed from the phone view — the mobile list shows only Last name,
        // First name, Email, Phone, and Plant(s); the rest live on the team
        // member's detail page.
        mobileHidden: true,
        // Stored value is the legal "First Last" form; resolve it against
        // the roster so any nickname the RM has set shows up here too.
        sortValue: (r) =>
          (displayStoredName(roster, r.RegionalManager) || '').toLowerCase(),
        render: (r) => {
          const shown = displayStoredName(roster, r.RegionalManager);
          if (!shown) return <span className="muted">—</span>;
          return shown;
        },
      },
      {
        key: 'liaisonManagers',
        label: 'Liaison Manager',
        mobileHidden: true,
        // liaisonManagers is an array of names; join with commas for a
        // readable cell, and sort by the joined string so the column sorts
        // alphabetically on the primary LM. Each name is resolved through
        // the roster so nicknames render in place of legal first names.
        sortValue: (r) =>
          (r.liaisonManagers || [])
            .map((n) => displayStoredName(roster, n))
            .join(', '),
        render: (r) => {
          const lms = r.liaisonManagers || [];
          if (lms.length === 0) return <span className="muted">—</span>;
          return lms.map((n) => displayStoredName(roster, n)).join(', ');
        },
      },
      {
        key: 'plantCodes',
        label: 'Plant(s)',
        filter: false,
        mobileHidden: true,
        sortValue: (r) => (r.plantCodes || []).length,
        render: (r) => {
          const codes = r.plantCodes || [];
          if (codes.length === 0) return <span className="muted">—</span>;
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {codes.map((code) => {
                const p = plantByCode[code];
                return (
                  <span
                    key={code}
                    className="badge plant"
                    title={p?.Title || code}
                  >
                    {code}
                  </span>
                );
              })}
            </div>
          );
        },
      },
      // Last Updated — SharePoint's Modified system column. Sort key is
      // the parsed timestamp so newest/oldest ordering is chronological.
      // Falls back to Created for rows that haven't been edited since.
      {
        key: 'Modified',
        label: 'Last Updated',
        mobileHidden: true,
        sortValue: (r) => new Date(r.Modified || r.Created || 0).getTime(),
        render: (r) => {
          const v = r.Modified || r.Created;
          return v ? formatDateShort(v) : <span className="muted">—</span>;
        },
      },
    ],
    [plantByCode, roster],
  );

  // Exited Team Members table: same columns, but "Last Updated" becomes
  // "End Date" (per Jillian, Oct 2026), read from the End Date saved
  // when the person was marked Exited. Uses the date part only so a
  // stored UTC midnight doesn't show as the day before.
  const exitDateParts = (r) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(r.EndDate || ''));
    return m ? { y: m[1], mo: m[2], d: m[3] } : null;
  };
  const exitedColumns = useMemo(
    () =>
      columns.map((c) =>
        c.key !== 'Modified'
          ? c
          : {
              key: 'EndDate',
              label: 'End Date',
              mobileHidden: true,
              sortValue: (r) => {
                const p = exitDateParts(r);
                return p ? Number(`${p.y}${p.mo}${p.d}`) : 0;
              },
              render: (r) => {
                const p = exitDateParts(r);
                return p ? (
                  `${p.mo}/${p.d}/${p.y.slice(2)}`
                ) : (
                  <span className="muted">—</span>
                );
              },
            },
      ),
    [columns],
  );

  // Columns actually rendered in the custom table below. On phone widths,
  // `mobileHidden` columns drop out so rows don't run off-screen; they still
  // index for search and show on the member's detail page. Mirrors the
  // shared DataTable's visible-column logic.
  const visibleColumns = useMemo(
    () => columns.filter((c) => !c.hidden && !(isMobile && c.mobileHidden)),
    [columns, isMobile],
  );

  // Staff-section overrides (per Jillian, Aug 2026). Staff aren't liaisons,
  // so Regional Manager / Liaison Manager / Plant(s) don't apply to them —
  // for the Staff group only we collapse those three columns into a single
  // "Job Title / Role" column (the member's free-text Role) and give that
  // section its own header row. On phone widths those three columns are
  // already hidden, so there's nothing to collapse and staff rows fall back
  // to the normal (trimmed) layout.
  const STAFF_SWAP_KEYS = ['RegionalManager', 'liaisonManagers', 'plantCodes'];
  const staffSwapVisible = visibleColumns.filter((c) =>
    STAFF_SWAP_KEYS.includes(c.key),
  );
  const staffColActive = staffSwapVisible.length > 0;
  const STAFF_HEADER_TD_STYLE = {
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--brand-muted, #64748b)',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    borderBottom: '1px solid var(--brand-border, #e5e7eb)',
    background: '#fff',
  };
  // Build the Staff section's own header cells: normal labels for the
  // columns it keeps, and a single "Job Title / Role" spanning the three
  // swapped-out columns.
  const renderStaffHeaderCells = () => {
    const out = [];
    let emitted = false;
    for (const c of visibleColumns) {
      if (STAFF_SWAP_KEYS.includes(c.key)) {
        if (!emitted) {
          emitted = true;
          out.push(
            <td
              key="staff-role-h"
              colSpan={staffSwapVisible.length}
              style={STAFF_HEADER_TD_STYLE}
            >
              Job Title / Role
            </td>,
          );
        }
        continue;
      }
      out.push(
        <td key={c.key} style={STAFF_HEADER_TD_STYLE}>
          {c.label}
        </td>,
      );
    }
    return out;
  };
  // Staff data-row cells: same collapse — one Job Title / Role cell in
  // place of the three swapped columns.
  const renderStaffRowCells = (row) => {
    const out = [];
    let emitted = false;
    for (const c of visibleColumns) {
      if (STAFF_SWAP_KEYS.includes(c.key)) {
        if (!emitted) {
          emitted = true;
          out.push(
            <td key="staff-role" colSpan={staffSwapVisible.length}>
              {row.Role ? row.Role : <span className="muted">—</span>}
            </td>,
          );
        }
        continue;
      }
      out.push(
        <td key={c.key}>{c.render ? c.render(row) : row[c.key] ?? ''}</td>,
      );
    }
    return out;
  };

  // Partition active members into labeled buckets by portal role so the page
  // reads as an org chart: Regional Managers, then Customer Liaison Managers,
  // then Liaisons, then everyone else as "Staff." Search is applied per-row
  // before bucketing so the counts and section visibility reflect the filter.
  const groups = useMemo(() => {
    const needle = (q || '').trim().toLowerCase();
    const matches = (row) => {
      if (!needle) return true;
      const bag = [
        row.LastName,
        row.FirstName,
        // Include nickname so typing "Art" finds Arthur Jenuwine even
        // though the underlying FirstName column stores "Arthur".
        row.Nickname,
        row.Email,
        row.Phone,
        row.Role,
        row.RegionalManager,
        (row.liaisonManagers || []).join(', '),
      ];
      return bag.some(
        (v) => v != null && String(v).toLowerCase().includes(needle),
      );
    };
    const defs = [
      { key: 'rm', label: 'Regional Managers', test: (m) => m.PortalRole === 'RegionalManager' },
      { key: 'clm', label: 'Customer Liaison Managers', test: (m) => m.PortalRole === 'LiaisonManager' },
      { key: 'liaison', label: 'Liaisons', test: (m) => m.PortalRole === 'Liaison' },
      // Catch-all: Directors, "None", and anything else fall here.
      { key: 'staff', label: 'Staff', test: () => true },
    ];
    const buckets = defs.map((d) => ({ ...d, rows: [] }));
    for (const row of activeMembers) {
      if (!matches(row)) continue;
      for (const b of buckets) {
        if (b.test(row)) {
          b.rows.push(row);
          break;
        }
      }
    }
    return buckets;
  }, [activeMembers, q]);

  const onRowClick = (row) => {
    if (row.id) navigate(`/team/${encodeURIComponent(row.id)}`);
  };

  return (
    <>
      <div className="page-header">
        <h1>{reportsFilter ? 'Your Team' : 'Proxemics Team'}</h1>
        <div className="actions">
          {isDirector && !isMobile && (
            <button className="btn btn-primary" onClick={onNew}>
              + Add Team Member
            </button>
          )}
        </div>
      </div>

      {reportsFilter && (
        <div
          className="card"
          style={{
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 14 }}>
            Showing only your team.
          </span>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => setSearchParams({})}
          >
            Show Everyone
          </button>
        </div>
      )}

      {loading && <Loading />}
      {error && (
        <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
          <div className="card-title">Couldn't Load Team</div>
          <p>{error.message}</p>
          <button className="btn btn-outline btn-sm" onClick={refresh}>
            Retry
          </button>
        </div>
      )}
      {!loading && !error && (
        <>
          {activeMembers.length === 0 ? (
            <DataTable
              rows={[]}
              columns={columns}
              onRowClick={onRowClick}
              searchable={false}
              emptyMessage="No team members yet. Click “+ Add Team Member” to add one."
            />
          ) : (
            // Single <table> with section banner rows between groups. Using
            // one table (rather than four) keeps every column auto-sized
            // against every row in the page, so columns line up cleanly
            // across Regional Managers, Liaison Managers, Liaisons, and
            // Staff instead of each section picking its own widths.
            <>
              {isMobile && (
                <MobileSearchBox
                  value={q}
                  onChange={setQ}
                  label="Search Team Members"
                />
              )}
              {!(isMobile && !q.trim()) && (
              <div className="table-wrapper">
                {!isMobile && (
                  <div className="table-toolbar">
                    <input
                      type="search"
                      placeholder="Search..."
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </div>
                )}
              <table className="data">
                <thead>
                  <tr>
                    {visibleColumns.map((c) => {
                      const active = sort?.key === c.key;
                      const sortable = isSortable(c);
                      return (
                        <th
                          key={c.key}
                          style={{
                            ...(c.width ? { width: c.width } : {}),
                            ...(sortable
                              ? { cursor: 'pointer', userSelect: 'none' }
                              : {}),
                          }}
                          onClick={sortable ? () => toggleSort(c) : undefined}
                          title={sortable ? 'Click to sort' : undefined}
                        >
                          {c.label}
                          {sortable && (
                            <span
                              aria-hidden="true"
                              style={{
                                marginLeft: 6,
                                opacity: active ? 1 : 0.25,
                                fontSize: 10,
                              }}
                            >
                              {active ? (sort.dir === 'asc' ? '▲' : '▼') : '▲▼'}
                            </span>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {groups.every((g) => g.rows.length === 0) ? (
                    <tr>
                      <td colSpan={visibleColumns.length} className="empty">
                        No team members match your search.
                      </td>
                    </tr>
                  ) : (
                    groups.map((g) => {
                      if (g.rows.length === 0) return null;
                      // Sort within the group so clicking a column header
                      // reorders rows inside each section without breaking
                      // the role-based grouping. Default (no sort) preserves
                      // the service-side LastName order.
                      const sortedRows = sortRows(g.rows);
                      return (
                        <Fragment key={g.key}>
                          <tr>
                            <td
                              colSpan={visibleColumns.length}
                              style={{
                                // Brand-orange soft/dark pair used for
                                // badges — carried over here so the four
                                // role-group banners (Regional Managers /
                                // Customer Liaison Managers / Liaisons /
                                // Staff) stand apart from the neutral
                                // row striping.
                                background: 'var(--brand-primary-soft)',
                                padding: '10px 12px',
                                fontSize: 13,
                                fontWeight: 600,
                                color: 'var(--brand-primary-dark)',
                                letterSpacing: '0.3px',
                                textTransform: 'uppercase',
                              }}
                            >
                              {g.label}
                              <span
                                style={{
                                  fontWeight: 400,
                                  marginLeft: 8,
                                  textTransform: 'none',
                                }}
                              >
                                ({g.rows.length})
                              </span>
                            </td>
                          </tr>
                          {/* Staff gets its own header row — different info
                              than the liaison-oriented sections above. */}
                          {g.key === 'staff' && staffColActive && (
                            <tr>{renderStaffHeaderCells()}</tr>
                          )}
                          {sortedRows.map((row, idx) => (
                            <tr
                              key={row.id}
                              onClick={() => onRowClick(row)}
                              style={{
                                background: idx % 2 === 1 ? '#f7f8fa' : '#ffffff',
                                cursor: 'pointer',
                              }}
                            >
                              {g.key === 'staff' && staffColActive
                                ? renderStaffRowCells(row)
                                : visibleColumns.map((c) => (
                                    <td key={c.key}>
                                      {c.render
                                        ? c.render(row)
                                        : row[c.key] ?? ''}
                                    </td>
                                  ))}
                            </tr>
                          ))}
                        </Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
              </div>
              )}
            </>
          )}
          {canSeeExited && onLeaveMembers.length > 0 && (
            <details style={{ marginTop: 16 }}>
              <summary
                style={{
                  cursor: 'pointer',
                  padding: '8px 0',
                  color: 'var(--brand-muted, #64748b)',
                  fontWeight: 500,
                }}
              >
                Team Members On Leave ({onLeaveMembers.length})
              </summary>
              <div style={{ marginTop: 8 }}>
                <DataTable
                  rows={onLeaveMembers}
                  columns={columns}
                  onRowClick={onRowClick}
                  emptyMessage="No team members on leave."
                />
              </div>
            </details>
          )}
          {canSeeExited && exitedMembers.length > 0 && (
            <details style={{ marginTop: 16 }}>
              <summary
                style={{
                  cursor: 'pointer',
                  padding: '8px 0',
                  color: 'var(--brand-muted, #64748b)',
                  fontWeight: 500,
                }}
              >
                Exited Team Members ({exitedMembers.length})
              </summary>
              <div style={{ marginTop: 8 }}>
                <DataTable
                  rows={exitedMembers}
                  columns={exitedColumns}
                  onRowClick={onRowClick}
                  emptyMessage="No exited team members."
                />
              </div>
            </details>
          )}
        </>
      )}

      <Modal
        open={creating}
        onClose={() => (saving ? null : setCreating(false))}
        title="New Team Member"
      >
        {creating && (
          <>
            {saveError && (
              <div
                className="error"
                style={{
                  marginBottom: 12,
                  padding: '8px 12px',
                  background: '#fde8e8',
                  color: '#b91c1c',
                  borderRadius: 6,
                  fontSize: 13,
                }}
              >
                {saveError}
              </div>
            )}
            <TeamForm
              initial={{}}
              plants={plants}
              team={data || []}
              saving={saving}
              onSave={onSave}
              onCancel={() => setCreating(false)}
            />
          </>
        )}
      </Modal>

      <EntraReminderModal
        member={createdMember}
        onClose={() => setCreatedMember(null)}
      />
    </>
  );
}

/**
 * Post-save confirmation. Shown once a team member is successfully created;
 * nudges the director to also assign the portal role in Entra so the member
 * can actually sign in. Closes back to the list without any other action.
 */
function EntraReminderModal({ member, onClose }) {
  if (!member) return null;
  const entraUrl =
    'https://entra.microsoft.com/#view/Microsoft_AAD_IAM/ActiveDirectoryMenuBlade/~/Overview';
  const fullName = displayFullName(member);
  const roleLabel =
    member.PortalRole && member.PortalRole !== 'None'
      ? member.PortalRole.replace(/([A-Z])/g, ' $1').trim()
      : null;
  return (
    <Modal
      open={!!member}
      onClose={onClose}
      title="Team Member Added"
      hideHeaderClose
    >
      <p>
        <strong>{fullName || 'Member'}</strong> is now in the team list.
      </p>
      {roleLabel ? (
        <p>
          You recorded their portal role as <strong>{roleLabel}</strong>. To
          actually grant them access, open Entra and assign the same role on
          the Proxemics Portal app — otherwise they won't be able to sign in.
        </p>
      ) : (
        <p>
          No portal role was recorded. If this person should be able to sign
          in to the portal, edit their record and set a portal role — and
          remember to assign the matching role in Entra as well.
        </p>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          marginTop: 16,
        }}
      >
        <button className="btn btn-outline" onClick={onClose}>
          Close
        </button>
        <a
          className="btn btn-primary"
          href={entraUrl}
          target="_blank"
          rel="noreferrer"
          onClick={onClose}
        >
          Open Entra
        </a>
      </div>
    </Modal>
  );
}
